import {environment} from '../../../../environments/environment';

import {Storage} from '../storage';

import {CacheItem} from './cache-item';

export class CacheCollection {
    /**
     * The property indicates whether the collection
     * should be store to localStorage/sessionStorage and get from it
     */
    private readonly inStorageCollection: boolean;
    private readonly storageCollectionKey: string;
    private readonly storageTtl: number | null;

    private items: CacheItem[];

    constructor() {
        this.items = [];
        this.inStorageCollection = environment.useStorage;
        this.storageTtl = environment.useStorage ? environment.storageTtl : null;
        this.storageCollectionKey = environment.randomGeneratedStorageCollectionKey;

        this.initItems();
    }

    private initItems(): boolean {
        this.items = [];

        if (!this.inStorageCollection) {
            return true;
        }

        /**
         * Check for existing collection
         */
        const storageCollection = Storage.getStorageItem(this.storageCollectionKey);

        if (!storageCollection) {
            return false;
        }

        /**
         * Parse storage collection
         */
        try {
            /**
             * Throw error if the storage collection is invalid
             */
            const parsedStorageCollection = JSON.parse(storageCollection);

            /**
             * Check if parsed collection is corrupted
             */
            if (!Array.isArray(parsedStorageCollection)) {
                return false;
            }

            this.items = parsedStorageCollection
              .map((item) => this.parseItem(item))
              .filter((item) => item !== null) as CacheItem[];

            this.syncStorageCollection();

            return true;
        } catch (exception) {
            return false;
        }
    }

    private parseItem(item: string | null): CacheItem | null {
        try {
            if (!item) {
              return null;
            }

            const parsedItem = JSON.parse(item);

            /**
             * Check for correct storage option properties
             */
            if (parsedItem.key && parsedItem.data && parsedItem.timestamp) {

                if (this.isItemExpired(parsedItem)) {
                    return null;
                }

                return new CacheItem(parsedItem.key, parsedItem.data, parsedItem.timestamp);
            }

            return null;
        } catch (exception) {
            return null;
        }
    }

    private syncStorageCollection(): void {
        if (this.inStorageCollection) {
            const serializedItems = this.items.map((item: CacheItem) => {
                return item.toString();
            });

            Storage.setStorageItem(this.storageCollectionKey, JSON.stringify(serializedItems));
        }
    }

    private isItemExpired(item: CacheItem): boolean {
      if (this.storageTtl) {
        const minimalTimestamp = Date.now() - this.storageTtl;

        /**
         * Check for expired data;
         */
        return (item.timestamp < minimalTimestamp);
      }

      // TODO:: check later what to return
      return true;
    }

    getItem(key: string): CacheItem {
        return this.items.filter((item: CacheItem) => {
            return item.key === key;
        })[0];
    }

    addItem(key: string, data: any): void {
        if (!this.hasItem(key)) {
            const cacheItem = new CacheItem(key, data);

            this.items.push(cacheItem);

            this.syncStorageCollection();
        }

        this.updateItem(key, data);
    }

    hasItem(key: string): boolean {
        const filteredItems = this.items.filter((item: CacheItem) => {
            return item.key === key;
        });

        if (filteredItems.length === 0) {
            return false;
        }

        const filteredItem = filteredItems[0];

        if (!this.isItemExpired(filteredItem)) {
            return true;
        }

        /**
         * If the date is expired, remove it from the collection and sync storage.
         */
        this.items = this.items.filter((item: CacheItem) => {
            return filteredItem !== item;
        });

        this.syncStorageCollection();

        return false;
    }

    updateItem(key: string, data: any): void {
        this.items = this.items.map((item: CacheItem) => {
            if (item.key === key) {
                item.data = data;
                item.timestamp = Date.now();
            }

            return item;
        });

        this.syncStorageCollection();
    }

    removeItem(key: string): void {
        Storage.removeStorageItem(key);
    }

    flush(): void {
        Storage.removeStorageItem(this.storageCollectionKey);
    }
}
